import { useState, useEffect, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Clock, AlertCircle, Stethoscope, Users, ArrowRight, ImagePlus, ClipboardList, Heart, Eye, Bell, Shield, Scale, CheckCircle, Star, Calendar, Apple, Book, Camera, Sparkles, Loader2, PawPrint, X, HeartPulse, Info, Crown, Check, XCircle } from 'lucide-react'
import { createClient } from '@supabase/supabase-js'
import { Alert, AlertDescription } from "@/components/ui/alert"
import confetti from 'canvas-confetti'
import { motion, AnimatePresence } from 'framer-motion'
import { useAnalysis } from '../AnalysisContext'
import { useNavigate, useLocation } from 'react-router-dom'
import { useUser } from "@clerk/clerk-react"
import UpgradeModal from '../pages/UpgradeModal'
import PricingModal from '../pages/PricingModal'
import axios from 'axios'
import PetAnalysisLoader from './PetAnalysisLoader';
import toast from 'react-hot-toast';
import { getUserPlan } from '@/utils/plans';
import WelcomeAnalysisModal from '../pages/WelcomeAnalysisModal';
import { cn } from "@/lib/utils";
import { uploadImages } from '@/server/utils/imageUtils';
import { trackPixelEvent, PIXEL_EVENTS } from '../utils/pixelService.js';
import { trackScanInitiated, trackScanCompleted } from '../utils/gaService.js';


const API_URL = import.meta.env.VITE_API_URL;

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

const formatDate = (date) => {
  const d = new Date(date);
  return d.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).replace(/[/]/g, '-');
};

const getAvailableAnalysisCount = (plan, reportCount) => {
  const PLAN_LIMITS = {
    'Free': 1,
    'Basic': 5,
    'Pro': 15,
    'Premium': Infinity
  };

  const totalAvailable = PLAN_LIMITS[plan] || 0;
  return Math.max(0, totalAvailable - reportCount);
};

const getAvailableAnalysisText = (plan, reportCount) => {
  const remaining = getAvailableAnalysisCount(plan, reportCount);

  if (plan === 'Premium') return 'Análisis ilimitados';
  
  if (remaining === 0) {
    return '0 análisis restantes';
  }

  return `${remaining} ${remaining === 1 ? 'análisis restante' : 'análisis restantes'}`;
};

const checkPremiumStatus = (user) => {
  if (!user) return false;
  if (!user.unsafeMetadata) return false;
  return user.unsafeMetadata.isPremium === true;
};

const LoadingSpinner = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm">
    <div className="flex flex-col items-center justify-center p-8 space-y-4">
      <motion.div
        className="relative w-16 sm:w-20 md:w-24 aspect-square"
        animate={{ rotate: 360 }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "linear"
        }}
      >
        {/* Outer spinning border */}
        <div className="absolute inset-0 rounded-full border-4 border-amber-200 border-t-amber-500" />

        {/* Inner paw print with scaling animation */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <PawPrint className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 text-amber-600" />
        </motion.div>
      </motion.div>
    </div>
  </div>
);


export function HealthInput({ className, onAnalysisComplete }) {
  const [files, setFiles] = useState([]);
  const [userContext, setUserContext] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { clearContext, updateAnalysis, updateAnalysisFull, updateImages, updateImagesContext, analysisData, userInput, imageUrls, petName, setPetName } = useAnalysis();
  const [reportCount, setReportCount] = useState(0);
  const { isSignedIn, isLoaded, user, reload } = useUser();
  const [showUpgradeModalReport, setShowUpgradeModalReport] = useState(false);
  const [error, setError] = useState(null);
  const isPremium = checkPremiumStatus(user);
  const [analysisType, setAnalysisType] = useState('quick');
  const [pollInterval, setPollInterval] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [petSelected, setPetSelected] = useState(false);
  const [previousPets, setPreviousPets] = useState(null);
  const [showTip, setShowTip] = useState(true);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  
  // Add refs to track API request states
  const hasLoadedReportCount = useRef(false);
  const isLoadingPets = useRef(false);
  const isCheckingStatus = useRef(false);
  const lastStatusCheck = useRef(0);

  const navigate = useNavigate();
  const location = useLocation();

  // Expanded tips array with more useful advice
  const tips = [
    "Incluye fotos desde diferentes ángulos para obtener un análisis más preciso. Recomendamos una foto frontal, lateral y desde arriba.",
    "Asegúrate de que la luz sea buena para que se vean claramente los detalles del pelaje y ojos de tu mascota.",
    "Las fotos de cuerpo completo ayudan a evaluar mejor la condición física de tu perro.",
    "Evita usar flash, ya que puede distorsionar los colores naturales del pelaje.",
    "Un fondo neutro y sin distracciones mejora la calidad del análisis.",
    "Si notas algún problema específico (como enrojecimiento, inflamación, etc.), incluye una foto de cerca de esa área.",
    "Mantén a tu mascota quieta para obtener imágenes nítidas. Puedes pedirle a alguien que te ayude.",
    "Si tu perro tiene algún problema de comportamiento, considera incluir información en la sección de contexto."
  ];

  // Rotate tips every 30 seconds
  useEffect(() => {
    const tipInterval = setInterval(() => {
      setCurrentTipIndex((prevIndex) => (prevIndex + 1) % tips.length);
    }, 30000);
    
    return () => clearInterval(tipInterval);
  }, []);

  // Effect to refresh user data when returning from Stripe
  useEffect(() => {
    const refreshUserData = async () => {
      // Check if we're returning from a payment flow
      const justPaid = sessionStorage.getItem('justCompletedPayment');
      const redirectTimestamp = sessionStorage.getItem('redirectTimestamp');
      const now = Date.now();
      const redirectTime = redirectTimestamp ? parseInt(redirectTimestamp, 10) : 0;
      const isRecentRedirect = now - redirectTime < 10000; // Within 10 seconds
      const isDogAnalyzerPage = location.pathname === '/dog-analyzer';
      
      if ((justPaid === 'true' || isRecentRedirect) && isLoaded && user && isDogAnalyzerPage) {
        try {
          await reload();
          // Refetch report count after user data refresh
          fetchReportCount();
          
          // Clear the flags if needed
          if (justPaid === 'true' || isRecentRedirect) {
            sessionStorage.removeItem('justCompletedPayment');
            sessionStorage.removeItem('redirectTimestamp');
          }
        } catch (error) {
          console.error('Error refreshing user data in HealthInput:', error);
        }
      }
    };

    refreshUserData();
  }, [isLoaded, user, reload, location.pathname]);

  // Add a useEffect to update the plan whenever user data changes
  useEffect(() => {
    if (isLoaded && user) {
      // Force a re-evaluation of the plan
      const currentPlan = getUserPlan(user);
      
      // Refetch report count when user data changes
      if (hasLoadedReportCount.current) {
        hasLoadedReportCount.current = false;
        fetchReportCount();
      }
    }
  }, [isLoaded, user, user?.unsafeMetadata]);

  const handleSelectPet = (pet) => {
    setPetName(pet.name);
    setPetSelected(true);
    setShowWelcomeModal(false);
    // Aquí podrías cargar también el historial del pet si existe
  };

  const handleNewAnalysis = (type) => {
    if (type === 'named') {
      // El usuario quiere agregar una mascota con nombre
      setShowWelcomeModal(false);
    } else {
      // El usuario quiere continuar sin nombre
      setPetName('');
      setShowWelcomeModal(false);
    }
  };

  useEffect(() => {
    const savedState = localStorage.getItem('analysisState');
    const loadPreviousPets = async () => {
      if (!user?.id || isLoadingPets.current) return;

      try {
        isLoadingPets.current = true;
        const response = await fetch(`/api/user/pets/${user.id}`);
        const data = await response.json();
        isLoadingPets.current = false;
        setPreviousPets(data.data);
        if (!imageUrls || imageUrls.length === 0) {
          setShowWelcomeModal(true);
        } else {
          setPetSelected(true);
        }
      } catch (error) {
        isLoadingPets.current = false;
        console.error('Error loading previous pets:', error);
      }
    };
    
    if (savedState == null) {
      loadPreviousPets();
    }
  }, [user?.id]);

  useEffect(() => {
    try {
      const savedState = localStorage.getItem('analysisState');
      if (savedState) {
        const { requestId, timestamp, progress } = JSON.parse(savedState);
        const timeElapsed = Date.now() - timestamp;

        if (timeElapsed < 5 * 60 * 1000 && requestId) {
          setRequestId(requestId);
          setIsLoading(true);
          setUploadProgress(progress || 55);
          simulateUploadProgress(progress || 55);
          startPolling(requestId);
        } else {
          clearAnalysisState();
        }
      }
    } catch (error) {
      clearAnalysisState();
    }
  }, []);

  useEffect(() => {
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        clearAnalysisState();
      }
    };
  }, [pollInterval]);

  useEffect(() => {
    const loadContextData = async () => {
      try {
        if (userInput) {
          setUserContext(userInput);
        }
        if (petName) {
          setPetName(petName);
        }
        if (imageUrls?.length > 0) {
          // Convert image URLs to Files
          const urlToFile = async (url) => {
            try {
              const response = await fetch(url);
              const blob = await response.blob();
              return new File([blob], `image-${Date.now()}.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
            } catch (error) {
              console.error('Error converting URL to File:', error);
              return null;
            }
          };

          const imageFiles = await Promise.all(imageUrls.map(urlToFile));
          const validFiles = imageFiles.filter(file => file !== null);
          setFiles(validFiles);
        }
      } catch (error) {
        console.error('Error loading context data:', error);
      }
    };

    loadContextData();
  }, [imageUrls, userInput, petName]);

  // Check report count
  useEffect(() => {
    const fetchReportCount = async () => {
      if (!user?.id || hasLoadedReportCount.current) return;

      try {
        hasLoadedReportCount.current = true;
        const response = await fetch(`/api/reports/count/${user?.id}`);
        const data = await response.json();
        setReportCount(data.reportCount);
      } catch (error) {
        hasLoadedReportCount.current = false;
        console.error('Error checking report count:', error);
        setError('Error checking report count');
      }
    };

    fetchReportCount();
    
    // Reset the flag when user changes
    return () => {
      hasLoadedReportCount.current = false;
    };
  }, [user?.id]);

  const plan = getUserPlan(user);
  const availableText = getAvailableAnalysisText(plan, reportCount);

  const startPolling = (id, maxAttempts = 100) => {
    let attempts = 0;
    setRequestId(id);
    saveAnalysisState(id);
    const newInterval = setInterval(async () => {
      attempts++;
      if (attempts >= maxAttempts) {
        clearInterval(newInterval);
        clearAnalysisState();
        setIsLoading(false);
        toast.error('Ocurrió un error al procesar las imágenes. Por favor, inténtalo de nuevo.');
        return;
      }
      await checkAnalysisStatus(id, newInterval);
    }, 7000);
    setPollInterval(newInterval);
  };

  const saveAnalysisState = (reqId, currentProgress) => {
    if (!reqId) return; // Don't save if no requestId

    localStorage.setItem('analysisState', JSON.stringify({
      requestId: reqId, // Save the actual requestId
      isLoading: true,
      timestamp: Date.now(),
      progress: currentProgress || uploadProgress
    }));
  };

  const clearAnalysisState = () => {
    localStorage.removeItem('analysisState');
  };


  const checkAnalysisStatus = async (id, intervalRef) => {
    // Prevent multiple status checks in quick succession
    const now = Date.now();
    if (isCheckingStatus.current || (now - lastStatusCheck.current < 5000)) {
      return;
    }
    
    try {
      isCheckingStatus.current = true;
      lastStatusCheck.current = now;
      
      const response = await fetch(`/api/analysis/status/${id}`);
      if (!response.ok) throw new Error('Failed to fetch status');
      const result = await response.json();
      
      if (result.error || result.status === 'ERROR') {
        clearInterval(intervalRef);
        clearAnalysisState();
        setIsLoading(false);
        setError(result.error?.message || 'Analysis failed');
        toast.error('Error procesando las imágenes. Por favor, inténtalo de nuevo.');
        isCheckingStatus.current = false;
        return;
      }

      if (result.status === 'SUCCESS') {
        clearInterval(intervalRef);
        
        // Update local state immediately for better UX
        setReportCount(prev => prev + 1);
        
        // Reset the report count flag to allow fetching the updated count
        hasLoadedReportCount.current = false;
        
        // Increment the report count on the server
        await axios.post(`/api/reports/increment/${user.id}`);
        
        // Fetch the updated report count after a delay
        setTimeout(() => {
          fetchReportCount();
        }, 1000);
        
        clearAnalysisState();
        setUploadProgress(100);
        setIsLoading(false);
        // Save context before navigating
        updateAnalysisFull(result.data, userContext, result.breedGuide);
        updateImages(result.images);
        
        // Track scan completed event
        trackPixelEvent(PIXEL_EVENTS.SCAN_COMPLETED, {
          content_name: 'Pet Analysis',
          content_type: analysisType,
          content_ids: [id],
          success: true
        });
        
        // Track scan completed event in Google Analytics
        trackScanCompleted({
          content_name: 'Pet Analysis',
          content_type: analysisType,
          content_id: id,
          success: true,
          user_id: user?.id
        });
        
        triggerConfetti();
        navigate('/overview');
        if (onAnalysisComplete) onAnalysisComplete(result.data);
      }
      
      isCheckingStatus.current = false;
    } catch (error) {
      isCheckingStatus.current = false;
      clearInterval(intervalRef);
      clearAnalysisState();
      setIsLoading(false);
      toast.error('Error procesando las imágenes. Por favor, inténtalo de nuevo.');
    }
  };

  // Add a separate function to fetch report count
  const fetchReportCount = async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(`/api/reports/count/${user?.id}`);
      const data = await response.json();
      setReportCount(data.reportCount);
    } catch (error) {
      console.error('Error fetching report count:', error);
    }
  };

  const triggerConfetti = () => {
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 999
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio)
      });
    }

    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  };

  const simulateUploadProgress = (startAt = 0) => {
    setUploadProgress(startAt);

    const intervalRef = setInterval(() => {
      setUploadProgress(prev => {
        const newProgress = prev < 20 ? prev + 0.3 :
          prev < 40 ? prev + 0.3 :
            prev < 60 ? prev + 0.2 :
              prev < 80 ? prev + 0.15 : prev + 0.1;

        // Save progress on each update
        saveAnalysisState(requestId, newProgress);

        if (newProgress >= 98) {
          clearInterval(intervalRef);
          return newProgress;
        }
        return newProgress;
      });
    }, 100);

    return () => clearInterval(intervalRef);
  };
  const handleFileUpload = (e) => {
    const uploadedFiles = Array.from(e.target.files || []);
    if (uploadedFiles.length + files.length <= 5) {
      setFiles(prev => [...prev, ...uploadedFiles]);
    }
  };

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
  };

  async function onSubmit(event) {
    event.preventDefault();
    setPetSelected(true);
    if (files.length === 0) return;

    if (analysisType == 'complete' && plan === 'Free') {
      toast('El análisis completo solo está disponible en cualquiera de nuestros planes', {
        icon: '⚠️',
        duration: 4000,
        style: {
          borderRadius: '10px',
          background: '#FFF8E1',
          color: '#B45309',
          border: '1px solid #FDE68A',
          fontWeight: 'bold'
        },
      });
      
      setTimeout(() => {
        setShowUpgradeModalReport(true);
      }, 1500);
      return;
    }

    if (plan === 'Free' && reportCount > 0) {
      toast('Has agotado tu análisis gratuito', {
        icon: '⚠️',
        duration: 4000,
        style: {
          borderRadius: '10px',
          background: '#FFF8E1',
          color: '#B45309',
          border: '1px solid #FDE68A',
          fontWeight: 'bold'
        },
      });
      
      setTimeout(() => {
        setShowUpgradeModalReport(true);
      }, 1500);
      return;
    }

    const availableCount = getAvailableAnalysisCount(plan, reportCount);

    if (availableCount <= 0) {
      toast('Has agotado tus análisis mensuales', {
        icon: '⚠️',
        duration: 4000,
        style: {
          borderRadius: '10px',
          background: '#FFF8E1',
          color: '#B45309',
          border: '1px solid #FDE68A',
          fontWeight: 'bold'
        },
      });
      
      setTimeout(() => {
        setShowUpgradeModalReport(true);
      }, 1500);
      return;
    }
 

    let progressCleanup = null;

    try {
      setError(null);
      setIsLoading(true);
      setShowWelcomeModal(false);
      updateImages([]);

      // Track scan initiated event
      trackPixelEvent(PIXEL_EVENTS.SCAN_INITIATED, {
        content_name: 'Pet Analysis',
        content_type: analysisType,
        num_items: files.length
      });
      
      // Track scan initiated event in Google Analytics
      trackScanInitiated({
        content_name: 'Pet Analysis',
        content_type: analysisType,
        num_items: files.length,
        user_id: user?.id
      });

      progressCleanup = simulateUploadProgress();

      // Start timing
      console.time('Image Upload Duration');
      const uploadStartTime = performance.now();

      // Upload images
      const imageUrls = await uploadImages(files, user?.id, petName);

      // End timing
      const uploadEndTime = performance.now();
      const uploadDuration = (uploadEndTime - uploadStartTime) / 1000; // Convert to seconds

      if (!imageUrls?.length) {
        throw new Error('No se pudieron cargar las imágenes');
      }

      updateImages(imageUrls);
      const result = await fetch(`${API_URL}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          images: imageUrls,
          userContext,
          analysisType: analysisType,
          userId: user?.id,
          petName: petName || null
        })
      });

      const response = await result.json();
      if (response.status === 'IN_PROGRESS') {
        startPolling(response.requestId);
      }

    } catch (error) {
      console.error('Analysis failed:', error);
      toast.error(
        'Ups, algo salió mal al procesar tu solicitud. Por favor, inténtalo nuevamente en unos momentos.'
      );
      
      // Track error event
      trackPixelEvent(PIXEL_EVENTS.SCAN_COMPLETED, {
        content_name: 'Pet Analysis',
        content_type: analysisType,
        success: false,
        error_message: error.message || 'Unknown error'
      });
      
      // Track error event in Google Analytics
      trackScanCompleted({
        content_name: 'Pet Analysis',
        content_type: analysisType,
        success: false,
        error_message: error.message || 'Unknown error',
        user_id: user?.id
      });
    }
  }
  if (!previousPets) {
    return <LoadingSpinner />
  }

  return (
    <>
      <WelcomeAnalysisModal
        isOpen={showWelcomeModal}
        onOpenChange={(newVal) => {
          if (!newVal) {
            setPetSelected(true);
          }
          setShowWelcomeModal(newVal)
        }}
        previousPets={previousPets}
        onSelectPet={handleSelectPet}
        onNewAnalysis={handleNewAnalysis}
        setPetSelected={setPetSelected}
        setFiles={setFiles}
        setUserContext={setUserContext}
        analysisType={analysisType}
        setAnalysisType={setAnalysisType}
        onSubmit={onSubmit}
      />
      <Card className={`${className} bg-white/80 shadow-lg border-none min-h-[calc(100vh-5rem)] sm:min-h-0 mb-16 md:mb-0`}>      <CardHeader className="bg-gradient-to-br from-orange-50/80 via-white to-amber-50/80 pb-6 px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col gap-5"
        >
          {/* Top Section with Logo and Analysis Count */}
          <div className="flex items-start justify-between">
            {/* Logo with Status */}
            <div className="relative">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-orange-50/80 p-3 rounded-2xl shadow-sm"
              >
                <PawPrint className="h-7 w-7 text-orange-500/90" />
                <div className="absolute -bottom-1 -right-1">
                  <span className="flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                  </span>
                </div>
              </motion.div>
            </div>

            {/* Analysis Count Badge */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 bg-white rounded-full py-2 px-4 shadow-sm border border-orange-100/50"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="h-4 w-4 text-orange-400" />
              </motion.div>
              <span className="text-sm font-medium text-orange-700">
                {availableText}
              </span>
            </motion.div>
          </div>

          {petName ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-2"
            >
              <motion.h2
                className="text-2xl sm:text-3xl font-bold text-orange-800/90 leading-tight"
              >
                ¿Cómo se encuentra {petName} el día hoy?
              </motion.h2>
              <p className="text-lg text-amber-600/90">
                Análisis instantáneo de tu peludo potenciado por IA
              </p>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-2"
            >
              <motion.h2
                className="text-2xl sm:text-3xl font-bold text-orange-800/90 leading-tight"
              >
                ¿Cómo está tu peludo hoy?
              </motion.h2>
              <p className="text-lg text-amber-600/90">
                Análisis instantáneo potenciado por IA
              </p>
            </motion.div>
          )}
        </motion.div>
      </CardHeader>

        {petSelected && (<CardContent className="p-4 sm:p-6 space-y-5 sm:space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3 sm:space-y-4"
          >
            <div className="flex flex-wrap justify-between items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-2 sm:gap-3">
                <Camera className="h-5 w-5 sm:h-6 sm:w-6 text-amber-500" />
                <div className="flex flex-col space-y-1 sm:space-y-1.5">
                  <Label className="text-gray-800 font-medium text-sm sm:text-base md:text-lg">
                    Sube fotos de tu mascota
                  </Label>
                  <p className="text-xs sm:text-sm text-gray-600 leading-snug sm:leading-relaxed max-w-[90%] sm:max-w-full">
                    Para un mejor análisis, incluye fotos de cuerpo completo desde diferentes ángulos
                  </p>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="flex items-center gap-2 sm:gap-3"
              >
                <span className="text-sm sm:text-base text-amber-600 font-medium">
                  {files.length}/5 fotos
                </span>
                <div className="h-2 w-14 sm:w-16 bg-amber-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-amber-500 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${(files.length / 5) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </motion.div>
            </div>

            {/* Tip Box - Enhanced version */}
            {showTip && (
              <motion.div 
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.3 }}
                className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 mt-2 mr-2">
                  <button 
                    onClick={() => setShowTip(false)} 
                    className="text-blue-400 hover:text-blue-600 transition-colors"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="flex gap-3">
                  <div className="mt-0.5">
                    <div className="bg-blue-100 p-2 rounded-full">
                      <Info className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1 pr-6">
                    <h4 className="font-medium text-blue-700 text-sm sm:text-base mb-1">Consejo:</h4>
                    <p className="text-blue-600 text-sm leading-relaxed">
                      {tips[currentTipIndex]}
                    </p>
                    
                    <div className="mt-2 flex items-center gap-1">
                      {tips.map((_, index) => (
                        <div 
                          key={index} 
                          className={`h-1.5 w-1.5 rounded-full transition-all duration-300 ${
                            index === currentTipIndex ? 'bg-blue-600 scale-110' : 'bg-blue-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {files.map((file, index) => (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.02 }}
                  key={index}
                  className="relative aspect-square group"
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Foto ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg sm:rounded-xl shadow-sm ring-2 ring-amber-100 group-hover:ring-amber-300 transition-all"
                  />
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    type="button"
                    onClick={() => removeFile(index)}
                    className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 p-1.5 sm:p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all hover:bg-red-600 shadow-lg"
                  >
                    <X className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  </motion.button>
                </motion.div>
              ))}

              {files.length < 5 && (
                <motion.label
                  whileHover={{ scale: 1.02 }}
                  animate={files.length === 0 ? {
                    boxShadow: [
                      "0 0 0 0 rgba(251, 191, 36, 0)",
                      "0 0 0 4px rgba(251, 191, 36, 0.2)",
                      "0 0 0 0 rgba(251, 191, 36, 0)"
                    ]
                  } : {}}
                  transition={files.length === 0 ? {
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  } : {}}
                  className="relative aspect-square cursor-pointer group"
                >
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileUpload}
                    className="absolute inset-0 opacity-0"
                    disabled={isLoading}
                  />
                  <div className={cn(
                    "flex flex-col items-center justify-center h-full rounded-lg sm:rounded-xl border-2 border-dashed bg-amber-50/30 transition-all p-3 sm:p-4",
                    files.length === 0
                      ? "border-amber-300 animate-subtle-bounce"
                      : "border-amber-200 group-hover:border-amber-400 group-hover:bg-amber-100/20"
                  )}>
                    <ImagePlus className={cn(
                      "w-6 h-6 sm:w-7 sm:h-7 transition-colors",
                      files.length === 0
                        ? "text-amber-500 animate-subtle-pulse"
                        : "text-amber-400 group-hover:text-amber-500"
                    )} />
                    <p className="mt-2 text-sm sm:text-base text-amber-600 text-center px-2 font-medium">
                      {files.length === 0 ? 'Sube las fotos' : 'Agregar más'}
                    </p>
                    <p className="text-xs sm:text-sm text-amber-500/80 mt-1">
                      {files.length === 0 ? 'Máximo 5 fotos' : `${5 - files.length} restantes`}
                    </p>
                  </div>
                </motion.label>
              )}
            </div>
          </motion.div>
          {files.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-3 sm:space-y-4 mt-4"
            >
              {/* Context Section */}
              <motion.div className="space-y-2 sm:space-y-3">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                  <ClipboardList className="h-5 w-5 sm:h-6 sm:w-6 text-amber-500" />
                  <Label className="text-gray-800 font-medium text-base sm:text-lg">
                    Contexto de la Evaluación
                  </Label>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-600 text-sm font-medium ml-auto sm:ml-0"
                  >
                    opcional
                  </motion.div>
                </div>
                <Textarea
                  value={userContext}
                  onChange={(e) => setUserContext(e.target.value)}
                  placeholder={`${petName ? `¿Has notado cambios en ${petName}?` : '¿Has notado cambios en tu mascota?'} Cuéntanos sobre su comportamiento, apetito, actividad física o cualquier otra observación relevante...`}
                  className="mt-1.5 resize-none border-2 border-amber-100 focus:border-amber-400 
          rounded-xl h-32 sm:h-28
          text-sm sm:text-base text-gray-600
          p-3 sm:p-4
          placeholder:text-sm sm:placeholder:text-base
          placeholder:text-gray-500
          focus:placeholder:text-gray-400
          transition-colors
          min-h-[8rem]"
                />
              </motion.div>
            </motion.div>
          )}

          {/* Analysis Type Selection */}
          {files.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-3"
            >
              {/* Header */}
              <div className="flex items-center gap-2.5 px-1">
                <div className="bg-gradient-to-r from-amber-400 to-orange-400 p-2 rounded-lg">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-800">Tipo de análisis</h2>
                  <p className="text-xs text-gray-500">Selecciona la opción que mejor se adapte a tus necesidades</p>
                </div>
              </div>

              {/* Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Quick Analysis Card */}
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setAnalysisType('quick')}
                  className={cn(
                    "group relative w-full rounded-2xl transition-all duration-200 p-4 pb-3 text-left",
                    analysisType === 'quick'
                      ? "ring-2 ring-amber-400 bg-amber-50/10"
                      : "border border-gray-100 bg-white hover:border-amber-100"
                  )}
                >
                  {/* Badge */}
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-600 border border-green-100">
                      <CheckCircle className="w-3 h-3" strokeWidth={2.5} />
                      Estándar
                    </span>
                  </div>

                  <div className="mt-6">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 rounded-xl bg-amber-100/50 flex items-center justify-center">
                          <HeartPulse className="w-5 h-5 text-amber-500" strokeWidth={2} />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-gray-800">Análisis de Salud</h3>
                          <div className="flex items-center gap-1 text-xs text-blue-600">
                            <Clock className="w-3 h-3" strokeWidth={2.5} />
                            Análisis IA en 1 minuto
                          </div>
                        </div>
                      </div>
                      
                      {analysisType === 'quick' ? (
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-amber-500 to-amber-400 flex items-center justify-center">
                          <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                        </div>
                      ) : (
                        <div className="w-6 h-6 rounded-lg border-2 border-gray-200" />
                      )}
                    </div>

                    {/* Features */}
                    <div className="grid gap-1.5">
                      {[
                        { icon: Eye, text: "Evaluación general de salud" },
                        { icon: AlertCircle, text: "Detección de anomalías" },
                        { icon: Stethoscope, text: "Reporte de salud básico" }
                      ].map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 py-2 px-2.5 rounded-xl bg-amber-50/20">
                          <feature.icon className="w-4 h-4 text-amber-500" strokeWidth={2} />
                          <span className="text-sm text-gray-600">{feature.text}</span>
                        </div>
                      ))}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-1.5">
                        <Users className="w-3 h-3 text-amber-500" strokeWidth={2} />
                        <span className="text-xs text-gray-500">Chequeo preventivo</span>
                      </div>
                      <motion.div
                        animate={analysisType === 'quick' ? { x: [0, 3, 0] } : {}}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="flex items-center gap-1 text-amber-500"
                      >
                        <span className="text-xs font-medium">Seleccionar</span>
                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                      </motion.div>
                    </div>
                  </div>
                </motion.button>

                {/* Complete Analysis Card */}
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setAnalysisType('complete')}
                  className={cn(
                    "group relative w-full rounded-2xl transition-all duration-200 p-4 pb-3 text-left",
                    analysisType === 'complete'
                      ? "ring-2 ring-orange-400 bg-orange-50/10"
                      : "border border-gray-100 bg-white hover:border-orange-100"
                  )}
                >
                  {/* Badge */}
                  <div className="absolute top-3 right-3">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-amber-500/90 to-orange-500/90 text-white">
                      <Crown className="w-3 h-3" strokeWidth={2.5} />
                      Premium
                    </span>
                  </div>

                  <div className="mt-6">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 rounded-xl bg-orange-100/50 flex items-center justify-center">
                          <Sparkles className="w-5 h-5 text-orange-500" strokeWidth={2} />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-gray-800">Análisis Integral</h3>
                          <div className="flex items-center gap-1 text-xs text-blue-600">
                            <Clock className="w-3 h-3" strokeWidth={2.5} />
                            Análisis IA + Guías personalizadas
                          </div>
                        </div>
                      </div>
                      
                      {analysisType === 'complete' ? (
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-orange-500 to-orange-400 flex items-center justify-center">
                          <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                        </div>
                      ) : (
                        <div className="w-6 h-6 rounded-lg border-2 border-gray-200" />
                      )}
                    </div>

                    {/* Features */}
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { icon: Heart, text: "Análisis avanzado" },
                        { icon: Book, text: "Guía de raza" },
                        { icon: Shield, text: "Plan de cuidados" },
                        { icon: Apple, text: "Guía nutricional" }
                      ].map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 py-2 px-2.5 rounded-xl bg-orange-50/20">
                          <feature.icon className="w-4 h-4 text-orange-500" strokeWidth={2} />
                          <span className="text-sm text-gray-600">{feature.text}</span>
                        </div>
                      ))}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-1.5">
                        <Star className="w-3 h-3 text-amber-400" strokeWidth={2} />
                        <span className="text-xs text-gray-500">Cuidado personalizado</span>
                      </div>
                      <motion.div
                        animate={analysisType === 'complete' ? { x: [0, 3, 0] } : {}}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="flex items-center gap-1 text-orange-500"
                      >
                        <span className="text-xs font-medium">Seleccionar</span>
                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                      </motion.div>
                    </div>
                  </div>
                </motion.button>
              </div>

              {/* Selection helper text */}
              {analysisType && (
                <motion.div 
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="px-3 py-2 bg-amber-50/50 border border-amber-100/50 rounded-xl flex items-center justify-center gap-1.5"
                >
                  <Info className="w-3.5 h-3.5 text-amber-500" />
                  <p className="text-xs text-amber-700">
                    {analysisType === 'quick' 
                      ? "Has seleccionado el análisis de salud para un chequeo preventivo" 
                      : "Has seleccionado el análisis integral para un cuidado personalizado"}
                  </p>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Modal */}
          <PricingModal
            isOpen={showUpgradeModalReport}
            onClose={() => setShowUpgradeModalReport(false)}
            plan={plan}
          />

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2" // Changed from mt-6 to mt-3
          >
            <Button
              className={`
      w-full font-medium shadow-lg rounded-xl transition-all duration-300
      h-12 sm:h-14 px-4 sm:px-6
      ${!analysisType || files.length === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : analysisType === 'quick'
                    ? 'bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600'
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
                } text-white
    `}
              disabled={isLoading || files.length === 0 || !analysisType}
              onClick={onSubmit}
            >
              {!isLoading && (
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  className="flex items-center justify-center gap-2 sm:gap-3"
                >
                  {analysisType === 'quick' ?
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5" /> :
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                  }
                  <span className="text-base sm:text-lg whitespace-nowrap">
                    Iniciar {analysisType === 'quick' ? 'Análisis Rápido' : 'Análisis Completo'}
                  </span>
                </motion.div>
              )}
            </Button>
          </motion.div>

          {isLoading && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4"
            >
              <PetAnalysisLoader progress={uploadProgress} files={files}/>
            </motion.div>
          )}
        </CardContent>)}

      </Card>
    </>

  );
}

export default HealthInput;
